import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { toast } from "react-toastify";
import CustomerLayout from "../../../layouts/CustomerLayout";
import Loader from "../../../components/utilities/Loader";
import SubscriptionHeader from "../../../components/subscription/SubscriptionHeader";
import PackageGrid from "../../../components/subscription/PackageGrid";
import PaymentConfirmation from "../../../components/subscription/PaymentConfirmation";
import { CreditCard, TrendingUp, Users, ArrowLeft, Phone, MessageCircle, Crown, Star, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import landingPageRequests from "../../../api/Guest/LandingPageRequests";
import { useDispatch, useSelector } from "react-redux";
import subscriptionRequests from "../../../api/Customer/SubscriptionRequests";
import { updateSubscriptionData } from "../../../redux/userSlice";
import { modifyPackagePrices } from "../../../utils/packageUtils";

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState("1 month");
  const [selectedPackageName, setSelectedPackageName] = useState(null);
  
  const subscriptionPlan = useSelector(
    (state) => state.user?.txtconnect_ng_user?.subscription_plan
  );
  const user = useSelector((state) => state.user?.txtconnect_ng_user);
  const isPostpaid = subscriptionPlan?.name === "Postpaid" || subscriptionPlan?.package_name === "Postpaid" || subscriptionPlan?.plan_name === "Postpaid";

  const durations = ["1 month", "2 months", "3 months", "No Expiry"];

  useEffect(() => {
    fetchSubscriptionPackages();
  }, []);

  // Refresh subscription data when component mounts (e.g., after returning from payment)
  useEffect(() => {
    const refreshSubscription = async () => {
      try {
        const response = await subscriptionRequests.getCurrentUserSubscriptionData();
        if (response.status_code === "000" && response.data) {
          // response.data is the user/subscription record: plan_name, units, subscription_plan, etc.
          const { subscription_plan, plan_name, units } = response.data;
          dispatch(updateSubscriptionData({
            subscriptionData: subscription_plan,
            plan_name,
            units,
          }));
        }
      } catch (error) {
        console.error("Error refreshing subscription:", error);
      }
    };
    refreshSubscription();
    // const shouldRefresh = sessionStorage.getItem("shouldRefreshSubscription");
    // if (shouldRefresh === "true") {
    //   refreshSubscription();
    //   sessionStorage.removeItem("shouldRefreshSubscription");
    // }
  }, []);

  const fetchSubscriptionPackages = async () => {
    try {
      setIsLoading(true);
      const response = await landingPageRequests.getSubscriptionPlans();
      if (response.data.status_code === "000") {
        const sortedPackages = response.data.data.sort((a, b) => {
          const order = { Bronze: 0, Silver: 1, Gold: 2, Platinum: 3 };
          return order[a.name] - order[b.name];
        });
        const modifiedPackages = modifyPackagePrices(sortedPackages);
        setPackages(modifiedPackages);
      } else {
        toast.error(response.data.reason);
      }
    } catch (error) {
      toast.error("Failed to load subscription packages");
      console.error("Error fetching packages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = (packageData, packageName) => {
    setSelectedPackageName(packageName);
    setSelectedPlan(packageData);
    setShowPaymentConfirmation(true);
  };

  const handleDowngrade = (packageData, packageName) => {
    setSelectedPackageName(packageName);
    setSelectedPlan(packageData);
    setShowPaymentConfirmation(true);
  };

  const handleBackToPlans = () => {
    setSelectedPackageName(null);
    setShowPaymentConfirmation(false);
    setSelectedPlan(null);
  };


  const handleProceedToPayment = () => {
    // TODO: Implement payment gateway integration
    toast.info("Payment gateway integration coming soon!");
    //console.log("Proceeding to payment for:", selectedPlan);
  };

  if (isLoading) {
    return <Loader />;
  }

  // Show payment confirmation if a plan is selected
  if (showPaymentConfirmation && selectedPlan) {
    return (
      <CustomerLayout>
        <PaymentConfirmation
          selectedPlan={selectedPlan}
          selectedPackageName={selectedPackageName}
          selectedDuration={selectedDuration}
          onBack={handleBackToPlans}
          onProceedToPayment={handleProceedToPayment}
        />
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="pt-20 min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-red-50">
        <div className="px-4 py-8 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate("/home")}
              className="flex gap-2 items-center mb-4 text-gray-600 transition-colors hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </button>

            {/* Only show SubscriptionHeader if NOT Postpaid */}
            {!isPostpaid && (
              <SubscriptionHeader
                currentPlan={subscriptionPlan}
                units={user?.units || subscriptionPlan?.units || 0}
                onRefresh={fetchSubscriptionPackages}
              />
            )}
          </div>
           {/* Postpaid Plan Display - Replaces header when current plan is Postpaid */}
           {isPostpaid && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <div className="overflow-hidden relative bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 rounded-2xl border-2 shadow-xl border-amber-300/50">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2 bg-amber-200/20"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2 bg-orange-200/20"></div>
                
                <div className="relative p-8">
                  <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                    {/* Left Section - Plan Info */}
                    <div className="flex-1">
                      <div className="flex gap-3 items-center mb-4">
                        <div className="flex justify-center items-center w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-lg">
                          <Crown className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <div className="flex gap-2 items-center mb-1">
                            <h3 className="text-2xl font-bold text-slate-900">Enterprise Plan</h3>
                            <span className="px-3 py-1 text-xs font-semibold text-amber-800 bg-amber-200 rounded-full border border-amber-300">
                              Customized Plan
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">Your personalized enterprise solution</p>
                        </div>
                      </div>
                      
                      {/* Plan Details Grid */}
                      <div className="grid grid-cols-2 gap-4 mt-6 md:grid-cols-4">
                        <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/80 border-amber-200/50">
                          <p className="mb-1 text-xs font-medium text-slate-600">Units Available</p>
                          <p className="text-xl font-bold text-slate-900">
                            {user?.units || subscriptionPlan?.units || 0}
                          </p>
                        </div>
                        <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/80 border-amber-200/50">
                          <p className="mb-1 text-xs font-medium text-slate-600">Billing Type</p>
                          <p className="text-xl font-bold text-slate-900">Customized Billing</p>
                        </div>
                        <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/80 border-amber-200/50">
                          <p className="mb-1 text-xs font-medium text-slate-600">Status</p>
                          <div className="flex gap-1 items-center">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <p className="text-xl font-bold text-green-600">Active</p>
                          </div>
                        </div>
                        <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/80 border-amber-200/50">
                          <p className="mb-1 text-xs font-medium text-slate-600">Plan Type</p>
                          <p className="text-xl font-bold text-slate-900">Enterprise</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Section - Premium Badge */}
                    <div className="flex justify-center md:justify-end">
                      <div className="p-6 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl border-2 shadow-lg border-amber-400/50">
                        <div className="text-center">
                          <Star className="mx-auto mb-2 w-12 h-12 text-white" />
                          <p className="mb-1 text-sm font-semibold text-white">Premium</p>
                          <p className="text-xs text-amber-100">Custom Solution</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Benefits Section */}
                  <div className="pt-6 mt-6 border-t border-amber-200/50">
                    <div className="flex flex-wrap gap-4">
                      <div className="flex gap-2 items-center px-4 py-2 rounded-lg border bg-white/60 border-amber-200/50">
                        <CheckCircle2 className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-slate-700">Flexible Billing</span>
                      </div>
                      <div className="flex gap-2 items-center px-4 py-2 rounded-lg border bg-white/60 border-amber-200/50">
                        <CheckCircle2 className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-slate-700">Dedicated Support</span>
                      </div>
                      <div className="flex gap-2 items-center px-4 py-2 rounded-lg border bg-white/60 border-amber-200/50">
                        <CheckCircle2 className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-slate-700">Custom Features</span>
                      </div>
                      <div className="flex gap-2 items-center px-4 py-2 rounded-lg border bg-white/60 border-amber-200/50">
                        <CheckCircle2 className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-slate-700">Priority Access</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Duration Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center items-center mb-8 w-full"
          >
            <div className="flex overflow-x-auto justify-center pb-2 -mb-2 w-full">
              <div className="inline-flex gap-2 p-2 bg-white rounded-full shadow-lg">
                {durations.map((duration) => {
                  const getDisplayText = (dur) => {
                    switch(dur) {
                      case "1 month": return { short: "1M", full: "1 Month" };
                      case "2 months": return { short: "2M", full: "2 Months" };
                      case "3 months": return { short: "3M", full: "3 Months" };
                      case "No Expiry": return { short: "∞", full: "No Expiry" };
                      default: return { short: dur, full: dur };
                    }
                  };
                  const text = getDisplayText(duration);
                  
                  return (
                    <button
                      key={duration}
                      onClick={() => setSelectedDuration(duration)}
                      className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                        selectedDuration === duration
                          ? "bg-red-600 text-white shadow-md"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <span className="sm:hidden">{text.short}</span>
                      <span className="hidden sm:inline">{text.full}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>

         

          {/* Available Plans */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <PackageGrid
              packages={packages}
              currentPlan={subscriptionPlan}
              onUpgrade={handleUpgrade}
              onDowngrade={handleDowngrade}
              units={user?.units || subscriptionPlan?.units || 0}
              selectedDuration={selectedDuration}
              userDuration={user?.plan_name || user?.subscription_duration || subscriptionPlan?.duration}
            />
          </motion.div>

          {/* Custom Plan Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-12"
          >
            <div className="overflow-hidden relative bg-gradient-to-br rounded-2xl border shadow-xl from-slate-800 via-slate-700 to-slate-900 border-slate-200">
              {/* Subtle background pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                  backgroundSize: '50px 50px'
                }}></div>
              </div>

              <div className="relative px-6 py-10 sm:px-12 sm:py-14">
                <div className="mx-auto max-w-4xl">
                  {/* Header */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mb-8 text-center"
                  >
                    <div className="inline-flex justify-center items-center px-4 py-2 mb-4 rounded-full border bg-slate-700/50 border-slate-600/50">
                      <span className="text-xs font-semibold tracking-wider uppercase text-slate-200">Custom Solutions</span>
                    </div>
                    <h2 className="mb-3 text-2xl font-bold text-white sm:text-3xl md:text-4xl">
                      Need a Custom Plan?
                    </h2>
                    <p className="mb-2 text-base text-slate-300 sm:text-lg">
                      Tailored solutions for your unique business needs
                    </p>
                    <p className="text-sm text-slate-400 sm:text-base">
                      Contact our team to discuss a personalized plan that fits your requirements
                    </p>
                  </motion.div>

                  {/* Contact Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8"
                  >
                    <div className="flex flex-col gap-4 p-6 bg-white rounded-xl border shadow-lg border-slate-200 sm:flex-row sm:items-center sm:justify-between">
                      {/* Phone Number Display */}
                      <div className="flex gap-4 items-center">
                        <div className="flex justify-center items-center w-14 h-14 rounded-xl bg-slate-100">
                          <Phone className="w-7 h-7 text-slate-700" />
                        </div>
                        <div>
                          <p className="mb-1 text-xs font-medium tracking-wide uppercase text-slate-500">Contact Us</p>
                          <p className="text-xl font-bold text-slate-900 sm:text-2xl">+233 55 548 8305</p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3 sm:ml-4">
                        {/* WhatsApp Button */}
                        <motion.a
                          href="https://wa.me/233272747529"
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex items-center gap-2 px-5 py-2.5 bg-[#25D366] text-white font-medium rounded-lg shadow-sm transition-all hover:shadow-md hover:bg-[#20BA5A]"
                        >
                          <MessageCircle className="w-5 h-5" />
                          <span className="hidden sm:inline">WhatsApp</span>
                          <span className="sm:hidden">WA</span>
                        </motion.a>

                        {/* Call Button */}
                        <motion.a
                          href="tel:+233272747529"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex gap-2 items-center px-5 py-2.5 font-medium text-white bg-slate-700 rounded-lg shadow-sm transition-all hover:shadow-md hover:bg-slate-800"
                        >
                          <Phone className="w-5 h-5" />
                          <span>Call Now</span>
                        </motion.a>
                      </div>
                    </div>
                  </motion.div>

                  {/* Benefits */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="grid grid-cols-1 gap-4 mt-8 sm:grid-cols-3"
                  >
                    <div className="flex gap-3 items-center text-slate-300">
                      <div className="flex justify-center items-center w-8 h-8 rounded-lg border bg-slate-700/50 border-slate-600/30">
                        <span className="text-sm font-semibold text-slate-200">✓</span>
                      </div>
                      <span className="text-sm font-medium">Flexible Pricing</span>
                    </div>
                    <div className="flex gap-3 items-center text-slate-300">
                      <div className="flex justify-center items-center w-8 h-8 rounded-lg border bg-slate-700/50 border-slate-600/30">
                        <span className="text-sm font-semibold text-slate-200">✓</span>
                      </div>
                      <span className="text-sm font-medium">Personalized Features</span>
                    </div>
                    <div className="flex gap-3 items-center text-slate-300">
                      <div className="flex justify-center items-center w-8 h-8 rounded-lg border bg-slate-700/50 border-slate-600/30">
                        <span className="text-sm font-semibold text-slate-200">✓</span>
                      </div>
                      <span className="text-sm font-medium">Dedicated Support</span>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 gap-6 mt-12 md:grid-cols-3"
          >
            <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
              <div className="flex gap-3 items-center mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Flexible Billing
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                Choose the plan that best fits your messaging needs. All plans
                include unlimited contact groups.
              </p>
            </div>

            <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
              <div className="flex gap-3 items-center mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Easy Scaling
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                Upgrade your plan anytime as your business grows. Changes take
                effect immediately.
              </p>
            </div>

            <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
              <div className="flex gap-3 items-center mb-4">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  24/7 Support
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                Get help whenever you need it. Our support team is available
                round the clock.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default SubscriptionPlans;